import {Injectable} from "@angular/core";
import {AkseptertSignaturmetode, BenyttetSigneringsmetode, HttpsUrl, SignaturinformasjonModell, Signeringsmetode} from "./ressurser/generertNg2HttpKlient";
import {i18n, Oversettelser} from "./i18n/Oversettelser";
import {Appkonfigurasjon} from "./Appkonfigurasjon";
import {lesZonedDateTimeSerialisert} from "../støttefunksjoner/temporal";
import {TemporalFormatering} from "./TemporalFormatering";
import {BildeRessurser} from "felles-ng2/tjenester/BildeRessurser";
import {caseForEnumMangler} from "felles-ng2/støttefunksjoner/enumer";

@Injectable({providedIn: 'root'})
export class DokumentSigneringInformasjon {

    static tilgjengeligeSigneringsmetoderEnkelElektronisk = [Signeringsmetode.lederplan_autentisert_tofakt_sms, Signeringsmetode.lederplan_autentisert_tofakt_epost]

    /** Returnerer en samling av signaturmodeller som inneholder ett innslag per {@link Signeringsmetode} */
    readonly signeringsmetodeModeller: SigneringsmetodeModell[];

    constructor(private konfigurasjon: Appkonfigurasjon, private oversettelser: Oversettelser, private temporalFormatering: TemporalFormatering, private bildeRessurser: BildeRessurser) {
        this.signeringsmetodeModeller = this._signeringsmetodeModeller();
    }

    signaturinformasjonSomTekst(signaturinformasjon: SignaturinformasjonModell, linjeskift = ' ') {
        const signaturtype = this.signaturtypeI18nNavn(signaturinformasjon.signaturtype);
        const signertDato = lesZonedDateTimeSerialisert(signaturinformasjon.signertDato);
        return this.oversettelser.oversett(i18n('Signert av {identitet}{linjeskift}{dato}{linjeskift}med {signaturtype}'), {
            linjeskift: linjeskift,
            signaturtype: signaturtype,
            identitet: signaturinformasjon.signertAv,
            dato: this.temporalFormatering.formaterZonedDateTime(signertDato, 'short', true)
        });
    }

    /** brukerorientert navn på signaturtype. NB må holdes i synk med tilsvarende i BenyttetSigneringsmetode.java */
    signaturtypeI18nNavn(signaturtype: BenyttetSigneringsmetode) {
        switch (signaturtype) {
            case BenyttetSigneringsmetode.legacy_autentisert:
            case BenyttetSigneringsmetode.sp2_enkel:
                return this.oversettelser.oversett(i18n('Enkel signatur som innlogget bruker'));
            case BenyttetSigneringsmetode.lederplan_autentisert_tofakt_sms:
                return this.oversettelser.oversett(i18n('Engangskode per SMS'));
            case BenyttetSigneringsmetode.lederplan_autentisert_tofakt_epost:
                return this.oversettelser.oversett(i18n('Engangskode per e-post'));
            case BenyttetSigneringsmetode.criipto_no_bankid_mobil_tekst:
                return this.oversettelser.oversett(i18n('Norsk BankID'));
            case BenyttetSigneringsmetode.criipto_no_bankid_central_tekst:
            case BenyttetSigneringsmetode.criipto_no_bankid_central_pdf:
            case BenyttetSigneringsmetode.criipto_no_bankid_pdf:
            case BenyttetSigneringsmetode.criipto_no_bankid_high_pdf:
            case BenyttetSigneringsmetode.legacy_no_bankid_central_pdf:
            case BenyttetSigneringsmetode.sp2_bankid:
            case BenyttetSigneringsmetode.bankid_qes:
                return this.oversettelser.oversett(i18n('Norsk BankID'));
            case BenyttetSigneringsmetode.criipto_se_bankid_samedevice:
            case BenyttetSigneringsmetode.criipto_se_bankid_anotherdevice:
            case BenyttetSigneringsmetode.criipto_se_bankid_anotherdevice_qr:
                return this.oversettelser.oversett(i18n('Svensk BankID'));
            default:
                caseForEnumMangler(signaturtype);
                console.warn('Ukjent BenyttetSigneringsmetode ' + signaturtype)
                return BenyttetSigneringsmetode[BenyttetSigneringsmetode.legacy_autentisert];
        }
    }

    signeringsmetoderSomModeller(metoder: Signeringsmetode[]): SigneringsmetodeModell[] {
        return this.signeringsmetodeModeller.filter(metode => metoder.includes(metode.metode))
    }

    signeringsmetodeSomModell(metode: Signeringsmetode): SigneringsmetodeModell {
        return this.signeringsmetodeModeller.find(kandidat => kandidat.metode == metode)!
    }

    /** Angir hvilke Signeringsmetode som er knyttet til angitt AkseptertSignaturmetode.
     * Merk at denne strukturen også er representert i java i AkseptertSignaturmetode.java - og må holdes i synk med denne */
    aksepterteSignaturmetoderSomModeller(metoder: AkseptertSignaturmetode[]): AkseptertSignaturmetodeModell [] {
        return metoder.map(metode => this.akseptertSignaturmetodeSomModell(metode))
          .sort((a, b) => a.sortering - b.sortering);
    }

    /** Angir hvilke Signeringsmetode som er knyttet til angitt AkseptertSignaturmetode.
     * Merk at denne strukturen også er representert i java i AkseptertSignaturmetode.java - og må holdes i synk med denne */
    akseptertSignaturmetodeSomModell(metode: AkseptertSignaturmetode): AkseptertSignaturmetodeModell {
        switch (metode) {
            case AkseptertSignaturmetode.avansertelektronisk:
                return {
                    metode: metode,
                    navn: this.oversettelser.oversett(i18n('Avansert elektronisk')),
                    sortering: 0,
                    signeringsmetoder: this.signeringsmetoderSomModeller(this.konfigurasjon.tilgjengeligeSigneringsmetoderAvansertElektronisk)
                };

            case AkseptertSignaturmetode.enkelelektronisk:
                return {
                    metode: metode,
                    navn: this.oversettelser.oversett(i18n('Enkel elektronisk')),
                    sortering: 1,
                    signeringsmetoder: this.signeringsmetoderSomModeller(DokumentSigneringInformasjon.tilgjengeligeSigneringsmetoderEnkelElektronisk)
                };

            default:
                caseForEnumMangler(metode);
                console.error(`Ukjent AkseptertSignaturmetode ${metode}`)
                return {
                    metode: metode,
                    navn: 'ukjent',
                    sortering: 2,
                    signeringsmetoder: []
                };
        }
    }

    //private hjelpemetoder

    private _signeringsmetodeModeller() {
        return [
            {
                metode: Signeringsmetode.lederplan_autentisert_tofakt_sms,
                navn: this.oversettelser.oversett(i18n('Engangskode per SMS')),
                logosti: null
            }, {
                metode: Signeringsmetode.lederplan_autentisert_tofakt_epost,
                navn: this.oversettelser.oversett(i18n('Engangskode per e-post')),
                logosti: null
            }, {
                metode: Signeringsmetode.norsk_bankid_netsentrisk_pdf,
                navn: this.oversettelser.oversett(i18n('Norsk BankID')),
                logosti: this.bildeRessurser.norskBankidLogo
            }, {
                metode: Signeringsmetode.norsk_bankid_netsentrisk_tekst,
                navn: this.oversettelser.oversett(i18n('Norsk BankID web')),
                logosti: this.bildeRessurser.norskBankidLogo
            },
            {
                metode: Signeringsmetode.svensk_bankid_samedevice,
                navn: this.oversettelser.oversett(i18n('Svensk BankID, på denne enheten')),
                logosti: this.bildeRessurser.svenskBankidLogo
            },
            {
                metode: Signeringsmetode.svensk_bankid_anotherdevice,
                navn: this.oversettelser.oversett(i18n('Svensk BankID, med personnummer')),
                logosti: this.bildeRessurser.svenskBankidLogo
            },
            {
                metode: Signeringsmetode.svensk_bankid_anotherdeviceqr,
                navn: this.oversettelser.oversett(i18n('Svensk BankID, med QR-kode')),
                logosti: this.bildeRessurser.svenskBankidLogo
            }
        ];
    }
}

/** Informasjon om en {@link AkseptertSignaturmetode},
 *  dvs en type signaturer som beskriver et sett av {@link Signeringsmetode} (representert av {@link SigneringsmetodeModell})*/
export interface AkseptertSignaturmetodeModell {
    metode: AkseptertSignaturmetode;
    navn: string;
    sortering: number;
    signeringsmetoder: SigneringsmetodeModell[]
}

/** Informasjon om en {@link Signeringsmetode} */
export interface SigneringsmetodeModell {
    metode: Signeringsmetode;
    logosti: HttpsUrl | null;
    navn: string;
}
